import React, { useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import VirusBeaconClient from '../../../clients/VirusBeaconClient';
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import useQuery from '../useQuery';
import Tooltip from '@mui/material/Tooltip';
import TablePagination from '@mui/material/TablePagination';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';

interface IProps {
  variant: any;
}

class VariantRow extends React.PureComponent<IProps> {
  render() {
    const { variant, countIndividual, aprox } = this.props;
    const { variantInternalId, variation, molecularAttributes } = variant || {};
    const { location, referenceBases, alternateBases } = variation || {};
    const { interval } = location || {};
    const { start } = interval;
    const { value } = start;
    const startBase1 = parseInt(value) + 1;
    const ref =
      referenceBases || variantInternalId.split(':').length > 1
        ? variantInternalId.split(':')[1]
        : '';
    const alt =
      alternateBases || variantInternalId.split(':').length > 2
        ? variantInternalId.split(':')[2]
        : '';

    const aminoacidChanges =
      molecularAttributes && molecularAttributes.aminoacidChanges
        ? molecularAttributes.aminoacidChanges.join(', ')
        : '';
    const geneIds =
      molecularAttributes && molecularAttributes.geneIds
        ? molecularAttributes.geneIds.join(', ')
        : '';
    const molecularEffects =
      molecularAttributes && molecularAttributes.molecularEffects
        ? molecularAttributes.molecularEffects.map((m) => m.label).join(', ')
        : '';

    return (
      <TableRow>
        <TableCell>{startBase1}</TableCell>
        <TableCell>{ref}</TableCell>
        <TableCell>{alt}</TableCell>
        <TableCell>{aminoacidChanges}</TableCell>
        <TableCell>{geneIds}</TableCell>
        <TableCell>{molecularEffects}</TableCell>
        <TableCell>
          {aprox && <span>~</span>} {countIndividual}
        </TableCell>
      </TableRow>
    );
  }
}

interface IProps {
  numVariants: any;
}

function InfoPosition() {
  return (
    <div className="tooltip">
      Chromosomal locations are 1-based interbase coordinates
    </div>
  );
}

const getNumIndividuals = (countIndividuals: any, id: string) => {
  return countIndividuals[id];
};

class VariantHead extends React.PureComponent<IProps> {
  render() {
    return (
      <TableHead>
        <TableRow>
          <TableCell>
            Position
            <Tooltip title={<InfoPosition />} placement="top-start" arrow>
              <sup>Base 1</sup>
            </Tooltip>
          </TableCell>
          <TableCell>Reference</TableCell>
          <TableCell>Alternative</TableCell>
          <TableCell>Aminoacid changes</TableCell>
          <TableCell>Gene Ids</TableCell>
          <TableCell>Molecular effects</TableCell>
          <TableCell>Num individuals</TableCell>
        </TableRow>
      </TableHead>
    );
  }
}

function ResultViewGenomic() {
  //const [existsVariants, setExistsVariants] = useState(false);
  const [numVariants, setNumVariants] = useState(null as number | null);
  const [variants, setVariants] = useState([] as any[]);
  const [countVariants, setCountVariants] = useState(0);
  const [loading, setLoading] = useState(false);

  const [message, setMessage] = useState(null);

  // Pagination
  const [page] = React.useState(0);
  const rowsPerPage = 100;

  const [countIndividuals, setCountIndividuals] = React.useState({});

  const query = useQuery();

  const updateCountIndividuals = (tempCountIndividuals: any, id: string) => {
    if (tempCountIndividuals[id] === undefined) {
      tempCountIndividuals[id] = 1;
      return true;
    } else {
      tempCountIndividuals[id] = tempCountIndividuals[id] + 1;
      return false;
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setMessage(null);
        setNumVariants(null);
        setVariants([]);
        query.pagination = {
          limit: rowsPerPage,
          skip: 0,
        };
        const [existsVariants, numVariants, variants] =
          await VirusBeaconClient.getVariants(query, abortController.signal);
        setNumVariants(numVariants);
        setCountVariants(variants.length);
        const tempCountIndividuals = {};
        const variantsUniq = variants.filter((v) =>
          updateCountIndividuals(
            tempCountIndividuals,
            v.identifiers.genomicHGVSId
          )
        );
        setCountIndividuals(tempCountIndividuals);
        setVariants(variantsUniq);
        setLoading(false);
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.log('ERROR: ' + error);
          setLoading(false);
          setMessage('Problem with server.');
        }
      }
    })();
    return () => {
      abortController.abort();
    };
  }, [query, setNumVariants, setVariants]);

  useEffect(() => {
    query.pagination = {
      ...TablePagination,
      skip: page,
    };
  }, [page]);

  return (
    <>
      <Box height="2em" />
      {variants.length > 0 && numVariants !== countVariants && (
        <Alert severity="success">
          Found <b>first</b> {variants.length} variant(s). Add more specific
          filters to narrow the results, avoid timeouts and get the exact number
          of individuals for a given genomic variant.
        </Alert>
      )}
      {variants.length > 0 && numVariants === countVariants && (
        <Alert severity="success">Founds {variants.length} variants.</Alert>
      )}
      <Table>
        {variants && variants.length > 0 && variants && (
          <VariantHead></VariantHead>
        )}
        <TableBody>
          {variants &&
            variants.length > 0 &&
            variants.map((v, idx) => (
              <VariantRow
                key={idx}
                variant={v}
                countIndividual={getNumIndividuals(
                  countIndividuals,
                  v.identifiers.genomicHGVSId
                )}
                aprox={variants.length > 0 && numVariants !== countVariants}
              />
            ))}
        </TableBody>
      </Table>
      {!loading && !message && variants.length === 0 && (
        <Alert>No variants founds. </Alert>
      )}
      {message && <>{message}</>}
      {loading && <CircularProgress />}
    </>
  );
}

export default ResultViewGenomic;
